import React, { useEffect, useState, useMemo } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import withRouter from "../../HOC/withRouter";
import { connect } from "react-redux";
import BrandificationImageFallback from "../BrandificationImageFallback";
import { getFallbackImageFromUrl, getImageFromUrl } from "../../utils/imageService";
import { authActionCreators } from "../../store/actions";
import { withTranslation } from "react-i18next";
import { clientActions } from "../../store/client/actions";
import { compose } from "react-recompose";
import { makeOppositeShade } from "../../utils/colorService";
import { isCommunityClient } from "../../utils/client";
import { DEFAULT_BOARD_NAME } from "../../utils/constants";
import MenuOpen from "../svgs/MenuOpen";
import MenuClose from "../svgs/MenuClose";
import "./styles.scss";
import { getTouchPointImage, getTouchPointImageRegular } from "../../utils/imageService";
import MenuLink from "./MenuLink";
import {
  IconArrow,
  IconBoard,
  IconBrandCommunity,
  IconBriefCase,
  IconCamera,
  IconChallenges,
  IconEarth,
  IconFeed,
  IconHome,
  IconJourney,
  IconLogout,
  IconQuiz,
  IconSurvey,
} from "./icons";
import { loginSwitch } from "../../utils/api";

const RWS_BRAND_CHALLENGE_CLIENT_ID = "661fa5a877372ae08e20e9f6";
const RWS_MERCHANSISING_CLIENT_ID = "661fa690540959dd883efac7";

function Mainmenu({ logout, t, client, orga, profile, quiz, survey, journeys, touchPoints, usersList, loginSuccess }) {
  const [opened, setOpened] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [, setWindowWidth] = useState(window.innerWidth);
  const isDesktop = window.matchMedia("(min-width: 1100px)")?.matches;
  const isCommunity = isCommunityClient(client);
  const location = useLocation();
  const isPdf = new URLSearchParams(location.search).get("isPDF") === "true";
  const navigate = useNavigate();

  useEffect(() => {
    document?.documentElement?.style?.setProperty("--menu-width", collapsed ? "100px" : "310px");
  }, [collapsed]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const toggleMenu = () => {
    if (!isDesktop) {
      setOpened(!opened);
    }
  };

  const isQuizVisible = useMemo(() => {
    return (
      quiz &&
      ((Array.isArray(quiz.unAnswered) && quiz.unAnswered.length > 0) ||
        (Array.isArray(quiz.answered) && quiz.answered.length > 0))
    );
  }, [quiz]);

  const isSurveyVisible = useMemo(() => {
    return (
      survey &&
      ((Array.isArray(survey.unAnswered) && survey.unAnswered.length > 0) ||
        (Array.isArray(survey.answered) && survey.answered.length > 0))
    );
  }, [survey]);

  const isOrgaVisible = useMemo(() => {
    let state = false;
    if (!orga.columns || !profile) {
      return state;
    }

    switch (orga.columns.visibility) {
      case "Public":
        state = true;
        break;
      case "Standard":
        state = orga.tasks.find(task => {
          return task.user && profile && task.user._id === profile._id;
        });
        break;
      case "Private":
        state = orga.tasks.find(task => {
          return task.user && profile && task.user._id === profile._id;
        });
        break;
      default:
        state = false;
    }
    return state;
  }, [orga, profile]);

  const isCommunityVisible = useMemo(() => {
    if (!usersList || usersList.length === 0) {
      return false;
    }
    const allUsersHidden = usersList.every(user => user.isHidden === true);
    return !allUsersHidden;
  }, [usersList]);

  const handleLogin = (clientId, clientName) => {
    loginSwitch({ client: clientId }).then(res => {
      loginSuccess({ token: res.data.token, clientId, clientName });
      navigate("/");
      setTimeout(() => window.location.reload(), 1);
    });
  };

  return (
    <div className="mainmenu-container">
      {!isPdf && (
        <>
          <div className="menu-toggle" onClick={toggleMenu}>
            {!opened && <MenuOpen color={client?.secondaryColor} />}
            {opened && <MenuClose color={client?.secondaryColor} />}
          </div>

          <div className={`mainmenus${opened ? " opened" : ""}`}>
            <div className="mainmenu">
              {window.innerWidth > 1100 ? (
                <div className="project-info">
                  <div className="logo-container">
                    <div className="logo-outter-circle">
                      <div className="logo-inner-circle">
                        <BrandificationImageFallback
                          src={getTouchPointImage(client)}
                          fallbackImage={getTouchPointImageRegular(client)}
                          className="logo"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="project-name">{client?.title}</div>
                    <div className="project-desc ">{client?.description}</div>
                  </div>
                </div>
              ) : (
                ""
              )}
              <div className="mobile-only client-profile">
                <Link to={`/profile`} onClick={toggleMenu}>
                  <BrandificationImageFallback
                    src={getImageFromUrl(profile?.photoURL)}
                    fallbackImage={getFallbackImageFromUrl(profile?.photoURL)}
                    className={`${profile?.photoURL ? "" : "no-image"}`}
                  />
                </Link>
              </div>
              <MenuLink
                Icon={IconHome}
                active={location.pathname === "/"}
                tooltip={t("menu.Home")}
                collapsed={collapsed}
                color={client?.primaryColor}
                onClick={toggleMenu}
                to="/"
              >
                {!collapsed && t("menu.Home")}
              </MenuLink>
              {!!touchPoints?.length && (
                <MenuLink
                  Icon={IconCamera}
                  active={location.pathname.indexOf("/touchpoint") > -1}
                  tooltip={t("menu.BrandTouchpoints", { lblTouchPoints: client?.lblTouchPoints })}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/touchpoint"
                  onClick={toggleMenu}
                >
                  {!collapsed && t("menu.BrandTouchpoints", { lblTouchPoints: client?.lblTouchPoints })}
                </MenuLink>
              )}
              {client?.ourBrand?.description && (
                <MenuLink
                  Icon={IconBriefCase}
                  active={location.pathname === "/our-brand"}
                  tooltip={t(isCommunity ? "menu.AppInfo" : "menu.BrandInfo")}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/our-brand"
                  onClick={toggleMenu}
                >
                  {!collapsed && t(isCommunity ? "menu.AppInfo" : "menu.BrandInfo")}
                </MenuLink>
              )}
              {client && client.ourBrand && (client.hasTasksCreated || client.ourBrand.tasks) && (
                <MenuLink
                  Icon={IconChallenges}
                  active={location.pathname === "/tasks"}
                  tooltip={t("menu.Tasks")}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/tasks"
                  onClick={toggleMenu}
                >
                  {!collapsed && t("menu.Tasks")}
                </MenuLink>
              )}
              {isQuizVisible && (
                <MenuLink
                  Icon={IconQuiz}
                  active={location.pathname === "/quiz"}
                  tooltip={t("menu.Quiz")}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/quiz"
                  onClick={toggleMenu}
                >
                  {!collapsed && t("menu.Quiz")}
                </MenuLink>
              )}
              {isOrgaVisible && (
                <MenuLink
                  Icon={IconBoard}
                  active={location.pathname === "/orga-board"}
                  tooltip={orga.columns.name}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/orga-board"
                  onClick={toggleMenu}
                >
                  {!collapsed && (orga.columns.name || DEFAULT_BOARD_NAME)}
                </MenuLink>
              )}
              {journeys?.length > 0 && (
                <MenuLink
                  className="desktop-only"
                  Icon={IconJourney}
                  active={location.pathname.includes("/journey")}
                  tooltip={t("menu.BrandJourney", "Brand Journey")}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/journey"
                  onClick={toggleMenu}
                >
                  {!collapsed && t("menu.BrandJourney", "Brand Journey")}
                </MenuLink>
              )}
              {client?._id !== RWS_MERCHANSISING_CLIENT_ID && isCommunityVisible && (
                <MenuLink
                  Icon={IconBrandCommunity}
                  active={location.pathname === "/community"}
                  tooltip={t("menu.BrandCommunity")}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/community"
                  onClick={toggleMenu}
                >
                  {!collapsed && t("menu.BrandCommunity")}
                </MenuLink>
              )}
              {isSurveyVisible && (
                <MenuLink
                  Icon={IconSurvey}
                  active={location.pathname === "/survey"}
                  tooltip={t("menu.Survey")}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/survey"
                  onClick={toggleMenu}
                >
                  {!collapsed && t("menu.Survey")}
                </MenuLink>
              )}
              {client && client.flocklerScript && (
                <MenuLink
                  Icon={IconFeed}
                  active={location.pathname === "/feed"}
                  tooltip={t("menu.BrandFeed")}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/feed"
                  onClick={toggleMenu}
                >
                  {!collapsed && t("menu.BrandFeed")}
                </MenuLink>
              )}
              {client?.customPage && (
                <MenuLink
                  Icon={IconEarth}
                  active={location.pathname === `/${client?.customPage?.slug}`}
                  tooltip={client?.customPage?.name}
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to={`/${client?.customPage?.slug}`}
                  onClick={toggleMenu}
                >
                  {!collapsed && client?.customPage?.name}
                </MenuLink>
              )}
              {client?._id === RWS_MERCHANSISING_CLIENT_ID && (
                <MenuLink
                  Icon={IconEarth}
                  active={false}
                  tooltip="RWS Brand Challenge"
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/"
                  onClick={() => handleLogin(RWS_BRAND_CHALLENGE_CLIENT_ID, "RWS Brand Challenge")}
                >
                  {!collapsed && "RWS Brand Challenge"}
                </MenuLink>
              )}
              {client?._id === RWS_BRAND_CHALLENGE_CLIENT_ID && (
                <MenuLink
                  Icon={IconEarth}
                  active={false}
                  tooltip="RWS Merchandising"
                  collapsed={collapsed}
                  color={client?.primaryColor}
                  to="/"
                  onClick={() => handleLogin(RWS_MERCHANSISING_CLIENT_ID, "RWS Merchandising")}
                >
                  {!collapsed && "RWS Merchandising"}
                </MenuLink>
              )}
              <div
                className={`logout`}
                onClick={() => {
                  logout();
                  if (profile?.ssoEnabled) {
                    navigate(`/sso/process/?logout=${profile.email}`);
                  }
                }}
              >
                <IconLogout secondaryColor={client?.secondaryColor} />
                {!collapsed && t("menu.Logout")}
              </div>
              <div
                className="iconArrow"
                onClick={() => setCollapsed(!collapsed)}
                style={{ transform: `rotate(${collapsed ? "180deg" : "0deg"})` }}
              >
                <IconArrow bgColorOppositeShade={makeOppositeShade(client?.bgColor)} />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const mapStateToProps = state => ({
  isAuthenticated: state.auth.isAuthenticated,
  client: state.client.client,
  orga: state.orga,
  profile: state.user.profile,
  quiz: state.quiz.quiz,
  survey: state.survey.survey,
  journeys: state.journeys,
  touchPoints: state.touchPoint.touchPoints,
  usersList: state.client.users,
});

export default compose(
  withRouter,
  connect(mapStateToProps, authActionCreators),
  connect(null, clientActions),
)(withTranslation()(Mainmenu));
